import { jwtDecode } from 'jwt-decode';
import moment from 'moment';
import _ from 'lodash';
import { ORDER_KEY, SYMBOL_NETWORK_TYPES } from '../consts/enums';

export function isLoggedIn() {
  return !!useCookie('accessToken').value;
}

export function normalizeEmail(email) {
  const [localPart, domainPart] = email.trim().toLowerCase().split('@');
  // Remove dots from the local part
  const localPartWithoutDots = localPart.replace(/\./g, '');

  // Reconstruct the email address
  return `${localPartWithoutDots}@${domainPart}`;
}

// Clear query params
export function clearUtmQuery(query) {
  return Object.fromEntries(Object.entries(query).filter(([key]) => key.startsWith('utm')));
}

export async function validateJwtToken(token) {
  const { t } = useI18n();
  if (!token) {
    return { error: t('token.missingToken') };
  }

  try {
    const scopes = jwtDecode(token).scopes || [];
    if (!['activate', 'reset'].some((scope) => scopes.includes(scope))) {
      return { error: 'Invalid scope' };
    }
  } catch (e) {
    console.error(e.message);
    return { error: t('token.invalidToken') };
  }
  if (Date.now() >= jwtDecode(token).exp * 1000) {
    return { error: t('token.expiredToken') };
  }

  return { valid: true };
}

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_FORMAT = 'DD.MM.yyyy';
export const DATETIME_FORMAT = 'DD.MM.YYYY HH:mm:ss';

export function formatDate(date, format = DATE_FORMAT) {
  return moment.utc(date).format(format);
}

export function formatDateTime(date, format = DATETIME_FORMAT) {
  return moment.utc(date).format(format);
}

// Returns exchange rate metadata that contains calculated exchange rate and source/base symbol used for the calculation
// Note that source/destination can be swapped depending whether baseSymbol is source or destination
export function getSymbolsExchangeRateMetadata(
  source,
  destination,
  baseSymbol,
  exchangeRate,
  sourceDecimals = 8,
  destinationDecimals = 8,
) {
  if (!source || !destination || !exchangeRate) return null;

  if (baseSymbol === source) {
    return {
      source,
      rate: _.round(exchangeRate, destinationDecimals),
      destination,
    };
  }
  return {
    source: destination,
    rate: _.round(1 / exchangeRate, sourceDecimals),
    destination: source,
  };
}

export async function waitFor(ms = 500) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Returns updated filter values from data parsed from query params
 */
export function getFilterFromQuery(query, defaultFilter) {
  // Keys of filter fields
  const filterFields = Object.keys(defaultFilter);

  // Convert query to filter
  return Object.keys(query).reduce(
    (acc, key) => {
      // If query has array as comma separated values, convert it to array
      let value = Array.isArray(defaultFilter[key]) ? query[key].split(',') : query[key];
      // Convert string values into proper type value, e.g. 'true' => true, '1' => 1
      let typeValue = parseFilterQueryValue(key, value);
      // If filter for value from query
      let filter = filterFields.includes(key) ? { [key]: typeValue } : {};

      return {
        ...acc,
        ...filter,
      };
    },
    { ...defaultFilter },
  );
}

/**
 * Returns single parsed query value that is read fom query
 */
function parseFilterQueryValue(key, value) {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (['limit', 'offset'].includes(key)) return Number(value);
  if (ORDER_KEY === key) return value;
  return value;
}

/**
 * Returns updated query object for router.
 */
export function getQueryFromFilter(filter, defaultFilter) {
  const filterFields = Object.keys(defaultFilter);

  // Convert filter to query
  return Object.keys(filter).reduce((acc, key) => {
    let queryFilter = {};
    let filterValue = filter[key];

    // Filter value is different from default
    if (filterFields.includes(key) && !isEqualWrapper(defaultFilter[key], filterValue)) {
      // If filter value is array, convert it to comma separated string
      if (Array.isArray(filterValue)) {
        queryFilter = { [key]: filterValue.toString() }; // comma separated array values
      } else {
        queryFilter = { [key]: filterValue };
      }
    }

    return {
      ...acc,
      ...queryFilter,
    };
  }, {});
}

/**
 * Returns serialized copy of the filter, ready to be sent to API
 */
export function serializeFilter(filter) {
  const arraylessFilter = _.cloneDeep(filter);
  const keys = Object.keys(arraylessFilter);
  keys.forEach((key) => {
    // If value is array with at least one element, convert it to comma separated string
    if (Array.isArray(arraylessFilter[key]) && arraylessFilter[key].length > 0) {
      arraylessFilter[key] = arraylessFilter[key].toString();
    }
  });
  return arraylessFilter;
}

function isEqualWrapper(value1, value2) {
  if (typeof value1 === 'number' || typeof value2 === 'number') {
    return _.isEqual(value1, value2);
  }
  return _.isEqual(value1, value2) || (_.isEmpty(value1) && _.isEmpty(value2));
}

/**
 * Returns internationalized network name. Must be used inside a vue component.
 */
export function getNetworkName(code) {
  const { t } = useI18n();
  const path = `networks['${code}']`;
  return t(path);
}

/**
 * Detect if network string refers to a testnet
 */
export function isTestnet(network) {
  return network.toUpperCase().includes(SYMBOL_NETWORK_TYPES.TESTNET);
}

export function formatAmount(amount) {
  // Convert to number because it can be type that does not have toString() function, for example Decimal128 from mongo
  if (isNumeric(amount)) {
    amount = Number(amount);
  }

  if (amount && amount.toString().includes('e')) {
    // If scientific notation is detected, convert to fixed-point format
    const decimalPlaces = Math.abs(Number(amount.toString().split('e')[1]));
    return amount.toFixed(decimalPlaces);
  }
  return amount;
}

function isNumeric(n) {
  const float = parseFloat(n);
  return !Number.isNaN(float) && Number.isFinite(float);
}

/**
 * @param to route name to navigate to
 * @param from nuxt route object, route to redirect to
 */
export async function navigateToWithRedirect(to, from) {
  // when page makes multiple requests all are processed and redirect can be set multiple times
  // skip adding redirect if there is already a redirect in the query
  const redirect = from.query.redirect || from.fullPath;
  return navigateTo({ name: to, query: { redirect: redirect } });
}
